import * as yup from 'yup';
import { set } from 'date-fns';
import { ERROR_MESSAGES, LIMITS } from 'utils/form';

enum LOYALTY_ACTIVITY_DEFINITION_TYPE {
  PRODUCTS_ONLY_ONCE = 'productsOnlyOnce',
  PRODUCTS_PER_ITEM = 'productsPerItem',
  ORDER_GROSS_AMOUNT = 'orderGrossAmount',
  JOINED_LOYALTY_PROGRAM = 'joinedLoyaltyProgram',
  ORDER_SOURCE = 'orderSource',
}

const LOYALTY_ACTIVITY_DEFINITION_TYPE_DICT: {
  [K: string]: string;
} = {
  [LOYALTY_ACTIVITY_DEFINITION_TYPE.PRODUCTS_ONLY_ONCE]:
    'Nagroda za zamówienie zawierające produkt z listy',
  [LOYALTY_ACTIVITY_DEFINITION_TYPE.PRODUCTS_PER_ITEM]:
    'Nagroda za każdą sztukę zamówionego produktu z listy',
  [LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_GROSS_AMOUNT]:
    'Nagroda za zamówienie o minimalnej wartości',
  [LOYALTY_ACTIVITY_DEFINITION_TYPE.JOINED_LOYALTY_PROGRAM]:
    'Dołączenie do programu lojalnościowego',
  [LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_SOURCE]: 'Zakupy w określonym źródle',
};

const LOYALTY_ACTIVITY_DEFINITION_TYPE_OPTIONS = Object.entries(
  LOYALTY_ACTIVITY_DEFINITION_TYPE_DICT,
).map(([key, value]) => ({
  label: value,
  value: key,
}));

const PRIZE_TYPE = {
  POINTS: 'points',
  TRANSACTIONS_VALIDITY_EXTENSION: 'transactionsValidityExtension',
};

const PRIZE_TYPE_DICT = {
  [PRIZE_TYPE.POINTS]: 'Punkty',
  [PRIZE_TYPE.TRANSACTIONS_VALIDITY_EXTENSION]: 'Wydłużanie ważności punktów',
};

const PRIZE_TYPE_OPTIONS = Object.entries(PRIZE_TYPE_DICT).map(
  ([key, value]) => ({
    label: value,
    value: key,
  }),
);

const TH_TRANSACTION_SOURCE = {
  IXPOS: 'iXPos',
  D365: 'D365',
};

const TH_TRANSACTION_SOURCE_DICT = {
  [TH_TRANSACTION_SOURCE.IXPOS]: 'Zakupy stacjonarne',
  [TH_TRANSACTION_SOURCE.D365]: 'Zakupy online',
};

const TH_TRANSACTION_SOURCE_OPTIONS = Object.entries(
  TH_TRANSACTION_SOURCE_DICT,
).map(([key, value]) => ({
  label: value,
  value: key,
}));

const defaultEndDate = set(new Date(), {
  hours: 23,
  minutes: 59,
  seconds: 59,
  milliseconds: 999,
});

const DEFAULT_VALUES = {
  name: '',
  activeFrom: undefined,
  activeTo: defaultEndDate,
  isOneTime: false,
  type: LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_GROSS_AMOUNT,
  minOrderGrossAmount: 0,
  allowedSkus: [],
  prizeType: PRIZE_TYPE.POINTS,
  points: 0,
  pointsValidityInDays: 365,
  prizeAvailableImmediately: false,
  transactionsValidityExtensionInDays: 1,
};

const BODY_SCHEMA = yup.object({
  name: yup.string().required(ERROR_MESSAGES.required),
  activeFrom: yup.date().nullable().required(ERROR_MESSAGES.required),
  activeTo: yup
    .date()
    .nullable()
    .required(ERROR_MESSAGES.required)
    .min(yup.ref('activeFrom'), ERROR_MESSAGES.invalidDateRange),
  isOneTime: yup.boolean(),

  type: yup
    .string()
    .oneOf(Object.values(LOYALTY_ACTIVITY_DEFINITION_TYPE))
    .required(ERROR_MESSAGES.required),
  minOrderGrossAmount: yup.number().when('type', {
    is: (type: LOYALTY_ACTIVITY_DEFINITION_TYPE) =>
      [
        LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_SOURCE,
        LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_GROSS_AMOUNT,
      ].includes(type),
    then: (schema) =>
      schema
        .typeError(ERROR_MESSAGES.invalidNumberType)
        .required(ERROR_MESSAGES.required),
  }),
  allowedSkus: yup
    .array()
    .of(yup.string())
    .when('type', {
      is:
        LOYALTY_ACTIVITY_DEFINITION_TYPE.PRODUCTS_ONLY_ONCE ||
        LOYALTY_ACTIVITY_DEFINITION_TYPE.PRODUCTS_PER_ITEM,
      then: (schema) => schema.min(1, ERROR_MESSAGES.required),
    }),
  prizeType: yup.string().required(ERROR_MESSAGES.required),
  points: yup.number().when('prizeType', {
    is: PRIZE_TYPE.POINTS,
    then: (schema) =>
      schema
        .typeError(ERROR_MESSAGES.invalidNumberType)
        .required(ERROR_MESSAGES.required),
  }),
  pointsValidityInDays: yup.number().when('prizeType', {
    is: PRIZE_TYPE.POINTS,
    then: (schema) =>
      schema
        .typeError(ERROR_MESSAGES.invalidNumberType)
        .required(ERROR_MESSAGES.required)
        .min(LIMITS.loyaltyActivityDefinition.minValidity)
        .max(
          LIMITS.loyaltyActivityDefinition.maxValidity,
          ERROR_MESSAGES.loyaltyActivityDefinition.maxValidity,
        ),
  }),
  prizeAvailableImmediately: yup.boolean(),
  transactionsValidityExtensionInDays: yup.number().when('prizeType', {
    is: PRIZE_TYPE.TRANSACTIONS_VALIDITY_EXTENSION,
    then: (schema) =>
      schema
        .typeError(ERROR_MESSAGES.invalidNumberType)
        .required(ERROR_MESSAGES.required)
        .min(
          LIMITS.loyaltyActivityDefinition.minValidity,
          ERROR_MESSAGES.loyaltyActivityDefinition.minValidity,
        )
        .max(
          LIMITS.loyaltyActivityDefinition.maxValidity,
          ERROR_MESSAGES.loyaltyActivityDefinition.maxValidity,
        ),
  }),
});

type FormData = yup.InferType<typeof BODY_SCHEMA>;

export {
  LOYALTY_ACTIVITY_DEFINITION_TYPE,
  LOYALTY_ACTIVITY_DEFINITION_TYPE_DICT,
  LOYALTY_ACTIVITY_DEFINITION_TYPE_OPTIONS,
  PRIZE_TYPE,
  PRIZE_TYPE_DICT,
  PRIZE_TYPE_OPTIONS,
  TH_TRANSACTION_SOURCE_OPTIONS,
  DEFAULT_VALUES,
  BODY_SCHEMA,
};
export type { FormData };
